import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, KeyRound, RotateCcw } from "lucide-react";
import { FileText } from "lucide-react";
import { api, ApiError } from "../../api/client";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { PASSWORD_HINT, passwordProblem } from "./ChangePasswordDialog";

// 以信裡的一次性連結設定新密碼。**公開頁面**,不受登入守衛管轄 ——
// 走到這一頁的人正是進不去的那一個。
//
// 四條規矩:
// 1. **token 只從查詢字串取,絕不印在畫面上。** 它就是一把鑰匙:印出來等於
//    旁邊經過的人、截圖與客服對話都拿得到,而它在一小時內都還開得了門。
// 2. **兩次新密碼不一致就不送出。** 送出去的話,使用者從此得用他打錯的那一個
//    登入,而他以為自己設的是另一個 —— 下一次就真的進不來了。
// 3. **後端的錯誤原句照抄。** 它刻意不分「偽造 / 過期 / 已用過」,前端不自己補
//    細節,否則那三種情況一被分開講,就成了一支 token 探測器。
// 4. **失敗一定要留下出路。** 只顯示一句「連結無效」而沒有「重新申請」,
//    使用者就卡死在一個永遠打不開的連結上。

/** 連結本身就不成立(網址沒帶 token)時的說法。 */
const MISSING_TOKEN = "這個重設連結不完整,可能是在信件裡被截斷了。請重新申請一次。";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const problem = passwordProblem(newPassword);
    if (problem !== null) {
      // 訊息只講規則,不回吐使用者打了什麼。
      setError(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("兩次輸入的新密碼不一致");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await api.post<void>("/auth/reset-password", { token, new_password: newPassword });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "無法連線到伺服器,請稍後再試。");
      setSubmitting(false);
    }
  };

  /** 重新申請的出路。連結壞掉時這是使用者唯一還走得通的路。 */
  const retryLink = (
    <Link
      to="/forgot"
      className="inline-flex items-center gap-1 text-sm text-[var(--accent)] underline"
    >
      <RotateCcw aria-hidden="true" size={14} />
      重新申請重設連結
    </Link>
  );

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--bg)] px-4 py-10">
      <div className="flex w-full max-w-sm flex-col gap-4 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6">
        <div className="flex items-center gap-2">
          <FileText aria-hidden="true" size={20} className="text-[var(--accent)]" />
          <span className="text-sm font-semibold text-[var(--text)]">星采設計內部系統</span>
        </div>

        <h1 className="m-0 text-xl font-semibold text-[var(--text)]">設定新密碼</h1>

        {done ? (
          <>
            <p
              role="status"
              className="m-0 rounded-[var(--radius)] border border-[var(--ok)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--text)]"
            >
              密碼已更新,請用新的密碼登入。
            </p>
            {/* 重設成功刻意不順手登入:確認新密碼真的記得住,也讓拿到信卻不知道
                密碼的人止步於此。 */}
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-[var(--accent)] underline"
            >
              <ArrowLeft aria-hidden="true" size={14} />
              回登入
            </Link>
          </>
        ) : token === "" ? (
          <>
            <p
              role="alert"
              className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
            >
              {MISSING_TOKEN}
            </p>
            {/* 表單一律不出現 —— 出現了就是請使用者填一份注定失敗的表單。 */}
            {retryLink}
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Input
                label="新密碼"
                type="password"
                name="new_password"
                autoComplete="new-password"
                autoFocus
                required
                hint={PASSWORD_HINT}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
              <Input
                label="再輸入一次新密碼"
                type="password"
                name="confirm_password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />

              {error ? (
                <p
                  role="alert"
                  className="m-0 rounded-[var(--radius)] border border-[var(--danger)] bg-[var(--surface-2)] px-2.5 py-2 text-sm text-[var(--danger)]"
                >
                  {error}
                </p>
              ) : null}

              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                icon={<KeyRound aria-hidden="true" size={16} />}
                className="mt-1"
              >
                設定新密碼
              </Button>
            </form>

            {error ? retryLink : null}
          </>
        )}
      </div>
    </div>
  );
}
